import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import quantstampLogo from '../assets/images/report-logo.svg'
import arrowRight from '../assets/images/arrow-right.svg'
import Hamburger from 'hamburger-react'
import Scrollspy from 'react-scrollspy'
import linkedinIcon from '../assets/images/linkedin.svg'
import twitterIcon from '../assets/images/twitter.svg'
import youtubeIcon from '../assets/images/youtube.svg'

const SOCIALS = [
  {
    to: 'https://www.linkedin.com/company/quantstamp',
    icon: linkedinIcon,
    alt: 'LinkedIn',
  },
  {
    to: 'https://twitter.com/quantstamp',
    icon: twitterIcon,
    alt: 'Twitter',
  },
  {
    to: 'https://www.youtube.com/@QuantstampHQ',
    icon: youtubeIcon,
    alt: 'Youtube',
  },
]

const Container = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 36px;
  background: white;
  z-index: 5;

  @media screen and (max-width: 1200px) {
    padding: 20px;
    position: sticky;
    top: 0;

    .hamburger-react {
      display: block !important;
    }
  }

  .hamburger-react {
    z-index: 2;
    display: none;
  }
`

const Backdrop = styled.div`
  position: absolute;
  height: 100vh;
  width: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const DraftBadge = styled.div`
  border-radius: 999px;
  font-weight: 500;
  font-size: 10px;
  line-height: 15px;
  padding: 1px 8px;
  background: rgba(170, 71, 0, 0.2);
  color: #aa4700;
`

const HambugerContainer = styled.div`
  padding: 1px 9px;
  position: relative;
  right: ${props => (props.open ? '-20px' : 'unset')};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 6px;
  }

  @media only screen and (min-width: 1200px) {
    display: none;
  }
`

const Links = styled(Scrollspy)`
  display: none;
  gap: 24px;
  box-sizing: border-box;
  word-break: break-all;

  @media only screen and (max-width: 1200px) {
    display: ${props => (!props.open ? 'none' : 'block')};
    position: absolute;
    top: 0;
    right: 0;
    width: 70%;
    max-width: 320px;
    height: 100vh;
    background: #101b38;
    padding: 74px 0px 50px 5px;
    z-index: 1;
    overflow: scroll;
  }
`

const Link = styled.a`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: white;

  @media only screen and (max-width: 1200px) {
    display: flex;
    font-size: 18px;
    line-height: 28px;
    margin-right: 5px;
    word-break: keep-all;
    white-space: pre-wrap;

    ${props =>
      props.active
        ? `
      color: #52C1FF;

      &:before {
        content: '•';
        color: #52c1ff;
        padding: 16px 12px;
      }
      `
        : `
      margin-left: 30px;
    `};

    &:hover {
      text-decoration: none;
    }
  }

  &:hover {
    text-decoration: underline;
  }
`

const Label = styled.span`
  @media only screen and (max-width: 1200px) {
    flex: 1;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: 16px 0;
    ${props => `
      display: ${props.display || 'inline'};
      margin: ${props.margin || '0'};
  `}
  }
`

const AuditRequestLink = styled.a`
  display: flex;
  text-decoration: none;
  gap: 1px;

  span {
    display: block;
    background: #e7eefb;
    padding: 17px 16px;
    font-family: 'SohneMono';
    font-size: 14px;
    line-height: normal;
    text-decoration: none;
    color: #0418d6;

    &:hover {
      background-color: rgba(231, 238, 251, 0.8);
    }
  }

  &:hover {
    text-decoration: none;
  }

  div {
    background: #e7eefb;
    padding: 16px;
  }

  @media screen and (max-width: 1200px) {
    display: none;
  }

  @media print {
    display: none;
  }
`

const SocialsContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-right: 5px;
  margin-left: 30px;
  margin-top: 25px;
`

const Navbar = ({ options, draft }) => {
  const [open, setOpen] = useState(false)
  const [activeOption, setActiveOption] = useState(null)

  useEffect(() => {
    if (document.getElementsByTagName('body')) {
      document.getElementsByTagName('body')[0].style.overflow = open ? 'hidden' : 'unset'
    }
  }, [open])

  return (
    <Container>
      {open && <Backdrop />}
      <LogoContainer>
        <a href="https://quantstamp.com">
          <img src={quantstampLogo} alt="Quantstamp" />
        </a>
        {draft ? <DraftBadge>DRAFT</DraftBadge> : null}
      </LogoContainer>
      <AuditRequestLink
        href="https://quantstamp.com/audits"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>Request a security audit</span>
        <div>
          <img src={arrowRight} alt="Go" />
        </div>
      </AuditRequestLink>
      <HambugerContainer open={open}>
        <Hamburger toggled={open} onToggle={e => setOpen(e)} color={open ? 'white' : 'black'} />
      </HambugerContainer>
      <Links
        items={options.map(opt => opt.to.replace('#', ''))}
        componentTag="div"
        onUpdate={e => setActiveOption(`#${e?.id}`)}
        open={open}
        currentClassName=""
      >
        {options.map(option =>
          !option.to ? (
            <Label margin="0 0 0 30px" display="flex">
              {option.label}
            </Label>
          ) : (
            <Link
              href={option.to}
              active={activeOption === option.to}
              onClick={() => setOpen(false)}
              key={`${option.to}-${option.label}`}
            >
              <Label>{option.label}</Label>
            </Link>
          ),
        )}
        <SocialsContainer>
          {SOCIALS.map(social => (
            <a href={social.to} key={social.to}>
              <img src={social.icon} alt={social.alt} />
            </a>
          ))}
        </SocialsContainer>
      </Links>
    </Container>
  )
}

Navbar.propTypes = {
  options: PropTypes.object,
  draft: PropTypes.bool,
}

export default Navbar
